import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import PageNotFound from './pages/pageNotFound';
import PageLoader from './components/loaders/loader1';
import MasterLayout from './pages/masterLayout';
import componentPageTitle from '@components/pageTitleUtils';
import useStore from './data/state/zustand';
import { withRouter } from './withRouter';
import { useLocation, useNavigate } from 'react-router-dom';
import Login from './authPages/login/index';
import OnlineReception from './pages/onlineReception';
import AutoLoginInvoiceView from './authPages/autoLoginInvoiceView';

const RouteWrapper = ({ component: Component, pageTitle, fixedRoute, dashboardRoute, itmPath, ...props }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const licence = useStore((state) => state.licence);
    const loggedUser = useStore((state) => state.authTokenInfo?.user);
    const roomInfos = useStore((state) => state.model.RoomInfo);

    useEffect(() => {
        // Call the onEnterRouteHandler logic
        const isHwUser =
            licence?.licence?.decodedLicence?.pack?.features?.includes('hwOnPrem') ||
            loggedUser?.role === 'hwAdmin';

        if (isHwUser && location.pathname === '/' && !location.search) {
            navigate('?rooms', { replace: true });
        }
        if (props.fixedRoute && props.fixedRoute !== '' && location.pathname !== props.fixedRoute) {
            navigate(fixedRoute, { replace: true });
        } else if (dashboardRoute && dashboardRoute !== '' && dashboardRoute !== '/' && location.pathname === '/') {
            navigate(dashboardRoute, { replace: true });
        } else if (
            (!props.locale || !props.defaultCurrency) &&
            window.location.hash.lastIndexOf('firstTimeSetup') === -1
        ) {
            if (roomInfos?.length === 0) {
                navigate('/firstTimeSetup', { replace: true });
            }
        }

        // Set the page title and path in the store
        useStore.getState().setNestedProperty(['pageTitle'], pageTitle);
        useStore.getState().setNestedProperty(['pagePath'], itmPath);
        // eslint-disable-next-line
    }, [location, navigate, licence, loggedUser, fixedRoute, dashboardRoute, props.locale, props.defaultCurrency, roomInfos]);

    return <Component {...props} />;
};

const PublicRoutes = (props) => {
    return (
        <Router {...props}>
            <MasterLayout {...props}>
                <Routes>
                    {componentPageTitle.map((itm, ind) => {
                        const ComponentWithRouter = withRouter(itm.component);
                        return (
                            <Route
                                key={ind}
                                path={itm.path}
                                element={
                                    <RouteWrapper
                                        component={ComponentWithRouter}
                                        pageTitle={window.translate(itm.pageTitle)}
                                        fixedRoute={props.fixedRoute}
                                        dashboardRoute={props.dashboardRoute}
                                        itmPath={itm.path}
                                        {...props}
                                    />
                                }
                            />
                        );
                    })}
                    <Route path="*" element={<PageNotFound />} />
                </Routes>
            </MasterLayout>
        </Router>
    );
};

const UnauthorizedRouter = (props) => (
    <Router {...props}>
        <Routes {...props}>
            <Route pageTitle="onlineReception" path="/onlineReception" element={<OnlineReception {...props} />} />
            <Route path="/" element={<Login {...props} showRegistration={props?.about?.adminUsersCount === 0} />} />
            <Route
                path="/login"
                element={<Login {...props} showRegistration={props?.about?.adminUsersCount === 0} />}
            />
            <Route 
                path="/invoiceView/:invoiceId" 
                element={<AutoLoginInvoiceView {...props} />} 
            />
            <Route path="*" element={<PageNotFound />} />
        </Routes>
    </Router>
);
const ExportRoutes = (props) => {
    const showConnectingScreen = useStore((appState) => appState.socketConnection?.showConnectingScreen);
    const socketConnection = useStore((appState) => appState.socketConnection);
    const about = useStore((appState) => appState.about);
    const locale = useStore((appState) => appState.locale);
    const defaultCurrency = useStore((appState) => appState.defaultCurrency);
    const hotelName = useStore((appState) => appState.companyInfo?.hotelName);
    const authTokenInfo = useStore((appState) => appState.authTokenInfo);
    const fixedRoute = useStore((appState) => appState.state.userPermissions?.fixedRoute);
    const dashboardRoute = useStore((appState) => appState.state.userPermissions?.dashboardRoute);
    const licenceType = useStore((appState) => appState.licence?.decodedLicence?.licenceType);
    const keycloakAuthenticated = useStore((appState) => appState.keycloak?.authenticated);

    const _props = {
        socketConnection,
        about,
        locale,
        defaultCurrency,
        hotelName,
        authTokenInfo,
        fixedRoute,
        dashboardRoute,
        licenceType,
        keycloakAuthenticated,
        showConnectingScreen,
    };
    const isOnlineReception = window.location.hash.includes('#/onlineReception');

    if (_props.authTokenInfo || _props.keycloakAuthenticated) {
        if (_props.socketConnection && _props.socketConnection.showConnectingScreen) {
            return (
                <PageLoader
                    header={_props.socketConnection.connectingMessage}
                    heading={_props.socketConnection.heading}
                    isOnlineReception={isOnlineReception}
                />
            );
        } else {
            return <PublicRoutes {..._props} />;
        }
    } else {
        if (_props.socketConnection?.showConnectingScreen) {
            return (
                <PageLoader
                    header={_props.socketConnection.connectingMessage}
                    heading={_props.socketConnection.heading}
                    isOnlineReception={isOnlineReception}
                />
            );
        } else {
            return <UnauthorizedRouter {..._props} />;
        }
    }
};

export default ExportRoutes;
